import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Factory, Smartphone, Users, Warehouse, LifeBuoy,
  GraduationCap, Wrench, ShieldCheck, ShoppingCart, SlidersHorizontal,
  Globe, LogOut, Menu, X, BellRing, Gauge, Cpu, ClipboardList, ClipboardCheck, CalendarRange, CalendarDays, UserCog, Layers, BarChart3, MessagesSquare, Target,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import NotificationBell from "@/components/NotificationBell";
import RequestChat from "@/components/RequestChat";

const NAV = [
  { key: "dashboard", to: "/dashboard", icon: LayoutDashboard, roles: ["management", "reparto_tecnico", "amministrazione", "sales", "warehouse"] },
  { key: "production", to: "/production", icon: Factory, roles: ["management", "reparto_tecnico", "amministrazione", "sales", "operator", "warehouse"] },
  { key: "gestione_produzione", to: "/gestione-produzione", icon: Target, roles: ["management", "reparto_tecnico", "amministrazione", "admin"] },
  { key: "gantt", to: "/gantt", icon: CalendarRange, roles: ["management", "reparto_tecnico", "amministrazione", "sales", "operator", "warehouse"] },
  { key: "calendar", to: "/calendar", icon: CalendarDays, roles: ["management", "reparto_tecnico", "amministrazione", "sales", "operator", "warehouse"] },
  { key: "operator", to: "/operator", icon: Smartphone, roles: ["management", "reparto_tecnico", "amministrazione", "operator"] },
  { key: "assignments", to: "/assignments", icon: ClipboardCheck, roles: ["management", "reparto_tecnico", "amministrazione"] },
  { key: "tasks", to: "/tasks", icon: BellRing, roles: ["management", "reparto_tecnico", "amministrazione", "operator", "sales", "warehouse"] },
  { key: "warehouse", to: "/warehouse", icon: Warehouse, roles: ["management", "reparto_tecnico", "amministrazione", "operator", "warehouse"] },
  { key: "raw_materials", to: "/raw-materials", icon: Layers, roles: ["management", "reparto_tecnico", "amministrazione", "operator", "warehouse"] },
  { key: "interventions", to: "/interventions", icon: ClipboardList, roles: ["management", "reparto_tecnico", "amministrazione", "operator", "sales", "warehouse"] },
  { key: "richieste_interne", to: "/richieste-interne", icon: MessagesSquare, roles: ["management", "reparto_tecnico", "amministrazione", "operator", "sales", "warehouse"] },
  { key: "products", to: "/products", icon: Cpu, roles: ["management", "reparto_tecnico", "amministrazione", "warehouse"] },
  { key: "productivity", to: "/productivity", icon: Gauge, roles: ["management", "reparto_tecnico", "amministrazione"] },
  { key: "support", to: "/support", icon: LifeBuoy, roles: ["management", "reparto_tecnico", "amministrazione", "sales", "operator", "warehouse"] },
  { key: "sales", to: "/sales", icon: Users, roles: ["management", "reparto_tecnico", "amministrazione", "sales"], soon: true },
  { key: "spare_parts", to: "/spare-parts", icon: Wrench, roles: ["management", "reparto_tecnico", "amministrazione", "sales"], soon: true },
  { key: "configurator", to: "/configurator", icon: SlidersHorizontal, roles: ["management", "reparto_tecnico", "amministrazione", "sales"] },
  { key: "academy", to: "/academy", icon: GraduationCap, roles: ["management", "reparto_tecnico", "amministrazione", "operator", "sales", "warehouse"] },
  { key: "quality", to: "/quality", icon: ShieldCheck, roles: ["management"] },
  { key: "purchasing", to: "/purchasing", icon: ShoppingCart, roles: ["management", "reparto_tecnico", "amministrazione", "warehouse"] },
  { key: "supply_checks", to: "/supply-checks", icon: ClipboardCheck, roles: ["management", "amministrazione", "warehouse"] },
  { key: "distributors", to: "/distributors", icon: Globe, roles: ["management", "reparto_tecnico", "amministrazione", "sales"], soon: true },
  { key: "analisi", to: "/analisi", icon: BarChart3, roles: ["management"] },
  { key: "pianificazione", to: "/pianificazione", icon: ClipboardList, roles: ["management"] },
  { key: "admin", to: "/admin", icon: UserCog, roles: ["management"] },
];

const GROUP_OF = {
  dashboard: "direzione", productivity: "direzione", analisi: "direzione", pianificazione: "direzione", quality: "direzione",
  production: "produzione", gestione_produzione: "produzione", gantt: "produzione", operator: "produzione", assignments: "produzione", interventions: "produzione", products: "produzione", richieste_interne: "produzione",
  warehouse: "logistica", raw_materials: "logistica", spare_parts: "logistica",
  sales: "commerciale", configurator: "commerciale", calendar: "commerciale", distributors: "commerciale",
  academy: "formazione", support: "formazione",
  tasks: "amministrazione", purchasing: "amministrazione", supply_checks: "amministrazione", admin: "amministrazione",
};
const GROUPS = [
  { key: "direzione", label: "Direzione & Analisi" },
  { key: "produzione", label: "Produzione" },
  { key: "logistica", label: "Logistica & Materiali" },
  { key: "commerciale", label: "Commerciale" },
  { key: "formazione", label: "Formazione & Supporto" },
  { key: "amministrazione", label: "Amministrazione" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [pendingPO, setPendingPO] = useState(0);
  const [pendingRI, setPendingRI] = useState(0);

  const canApprovePO = ["management", "admin", "amministrazione"].includes(user.role);
  useEffect(() => {
    if (!canApprovePO) return;
    api.get("/purchasing/pending-count").then((r) => setPendingPO(r.data?.pending || 0)).catch(() => {});
  }, [canApprovePO, location.pathname]);

  useEffect(() => {
    const load = () => api.get("/part-requests/unread-count").then((r) => setPendingRI(r.data?.count || 0)).catch(() => {});
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, [location.pathname]);

  let items;
  const assignExtra = (n) => n.key === "assignments" && user.can_assign;
  if (user.role === "admin" || user.role === "management") {
    items = NAV;
  } else if (user.role === "amministrazione") {
    // ruolo amministrativo: sempre nav in base al ruolo + eventuali extra concessi (mai ristretto sotto il baseline)
    items = NAV.filter((n) => n.roles.includes(user.role) || (user.permissions || []).includes(n.key) || assignExtra(n));
  } else if (user.permissions?.length) {
    items = NAV.filter((n) => user.permissions.includes(n.key) || (n.key === "admin" && ["admin", "management"].includes(user.role)) || assignExtra(n));
  } else {
    items = NAV.filter((n) => n.roles.includes(user.role) || user.role === "admin" || assignExtra(n));
  }

  const handleLogout = () => { logout(); navigate("/login"); };

  const Sidebar = (
    <aside className="w-[268px] shrink-0 flex flex-col bg-white border-r hairline h-full safe-top">
      <div className="px-6 py-5 border-b hairline">
        <img src="/abrams-logo.png" alt="ABRAMS" className="h-9 w-auto" />
        <div className="font-mono text-[10px] uppercase tracking-[0.28em] t-muted mt-2">Operating System</div>
      </div>

      <nav className="flex-1 overflow-y-auto py-4">
        {GROUPS.map((g) => {
          const gItems = items.filter((n) => (GROUP_OF[n.key] || "amministrazione") === g.key);
          if (gItems.length === 0) return null;
          return (
            <div key={g.key} className="mb-1.5">
              <div className="px-6 mt-3 mb-1 font-mono text-[10px] uppercase tracking-[0.2em] t-faint">{g.label}</div>
              {gItems.map((n) => (
                <NavLink
                  key={n.key}
                  to={n.to}
                  data-testid={`nav-${n.key}`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-6 py-3 lg:py-2.5 text-base lg:text-sm border-l-[3px] btn-industrial ${
                      isActive
                        ? "border-l-[#33559d] bg-[#eef2fb] text-[#2c4a89] font-500"
                        : "border-l-transparent text-[#35435a] hover:bg-[#f4f6f9] hover:text-[#12233f]"
                    }`
                  }
                >
                  <n.icon size={20} strokeWidth={1.9} />
                  <span className="flex-1">{t(n.key)}</span>
                  {n.key === "purchasing" && pendingPO > 0 && (
                    <span data-testid="nav-purchasing-badge" className="bg-[#9c1e2b] text-white font-700 text-[10px] rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                      {pendingPO}
                    </span>
                  )}
                  {n.key === "richieste_interne" && pendingRI > 0 && (
                    <span data-testid="nav-richieste-badge" className="bg-[#9c1e2b] text-white font-700 text-[10px] rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                      {pendingRI > 9 ? "9+" : pendingRI}
                    </span>
                  )}
                  {n.soon && (
                    <span className="font-mono text-[9px] uppercase tracking-wider t-faint border hairline px-1.5 py-0.5 rounded-[2px]">
                      {t("coming_soon")}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>

      <div className="border-t hairline p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 bg-[#33559d] flex items-center justify-center font-display font-700 text-white rounded-[3px]">
            {user.name?.[0]?.toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="text-sm t-head truncate" data-testid="sidebar-user-name">{user.name}</div>
            <div className="font-mono text-[10px] uppercase tracking-wider t-muted">{user.role}</div>
          </div>
        </div>
        <button data-testid="logout-button" onClick={handleLogout}
          className="btn-industrial w-full flex items-center justify-center gap-2 py-2 text-sm text-[#9c1e2b] border border-[#eec2c7] rounded-[3px] hover:bg-[#f9e7e9]">
          <LogOut size={16} /> {t("logout")}
        </button>
      </div>
    </aside>
  );

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: "var(--bg)" }}>
      <div className="hidden lg:flex h-full">{Sidebar}</div>

      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="h-full">{Sidebar}</div>
          <div className="flex-1 bg-black/40" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="shrink-0 flex items-center justify-between px-4 lg:px-8 border-b hairline bg-white/90 backdrop-blur-md z-20 safe-top" style={{ minHeight: "4rem" }}>
          <button className="lg:hidden t-head p-2 -ml-2" data-testid="mobile-menu-toggle" onClick={() => setOpen((o) => !o)}>
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
          <img src="/abrams-logo.png" alt="ABRAMS" className="lg:hidden h-7 w-auto" />
          <div className="hidden lg:block" />
          <div className="flex items-center gap-2">
            <NotificationBell />
            <div className="font-mono text-[11px] uppercase tracking-[0.15em] t-muted">{user.role}</div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto overflow-x-hidden safe-bottom">{children}</main>
      </div>
      <RequestChat />
    </div>
  );
}
